import type { TFunction } from 'i18next';
import i18n from '../../i18n';
import type { Notification, NotificationType } from '../../types/notifications';
import { formatNumber } from '../numberFormat';

type TranslateFn = TFunction | ((key: string, options?: Record<string, unknown>) => string);

function translate(key: string, options?: Record<string, unknown>, t?: TranslateFn): string {
  if (t) {
    return String(t(key, { ns: 'notifications', ...options }));
  }
  return String(i18n.t(key, { ns: 'notifications', ...options }));
}

function receiptSuffix(receipt?: string): string {
  return receipt ? ` (${receipt})` : '';
}

function trackingSuffix(trackingId?: string): string {
  return trackingId ? ` — ${trackingId}` : '';
}

/**
 * Reconstruit le message de notification à l'affichage (locale courante).
 * Fallback sur `notification.message` (legacy) si le type n'est pas géré.
 */
export function getNotificationMessage(
  notification: Pick<
    Notification,
    | 'type'
    | 'message'
    | 'productName'
    | 'currentStock'
    | 'clientName'
    | 'appointmentTitle'
    | 'appointmentTime'
    | 'amountRemaining'
    | 'saleReceiptNumber'
    | 'supportTicketSubject'
    | 'trackingId'
    | 'triggeredByUser'
    | 'employeeName'
    | 'siteName'
    | 'performanceMonth'
    | 'performanceYear'
    | 'createdCount'
  >,
  t?: TranslateFn
): string {
  const type = notification.type as NotificationType;
  const amount = formatNumber(notification.amountRemaining ?? 0);

  switch (type) {
    case 'low_stock':
      return translate(
        'low_stock',
        {
          productName: notification.productName ?? '',
          currentStock: notification.currentStock ?? 0,
        },
        t
      );
    case 'out_of_stock':
      return translate(
        'out_of_stock',
        { productName: notification.productName ?? '' },
        t
      );
    case 'birthday':
      return translate('birthday', { clientName: notification.clientName ?? '' }, t);
    case 'appointment_reminder_24h':
      return translate(
        'appointment_reminder_24h',
        {
          clientName: notification.clientName ?? '',
          appointmentTitle: notification.appointmentTitle ?? '',
          appointmentTime: notification.appointmentTime ?? '',
        },
        t
      );
    case 'appointment_reminder_2h':
      return translate(
        'appointment_reminder_2h',
        {
          clientName: notification.clientName ?? '',
          appointmentTitle: notification.appointmentTitle ?? '',
          appointmentTime: notification.appointmentTime ?? '',
        },
        t
      );
    case 'appointment_request':
      return translate(
        'appointment_request',
        { clientName: notification.clientName ?? '' },
        t
      );
    case 'pickup_request':
      return translate(
        'pickup_request',
        {
          clientName: notification.clientName ?? '',
          trackingSuffix: trackingSuffix(notification.trackingId),
        },
        t
      );
    case 'payment_plan_reminder_2d':
    case 'payment_plan_reminder_1d':
    case 'payment_plan_reminder_due':
      return translate(
        type,
        {
          clientName: notification.clientName ?? '',
          amount,
          receiptSuffix: receiptSuffix(notification.saleReceiptNumber),
        },
        t
      );
    case 'support_ticket_created':
      return translate(
        'support_ticket_created',
        {
          authorName: notification.triggeredByUser ?? notification.clientName ?? '',
          subject: notification.supportTicketSubject ?? '',
        },
        t
      );
    case 'support_ticket_reply':
    case 'support_ticket_resolved':
    case 'support_ticket_opened_for_you':
      return translate(
        type,
        { subject: notification.supportTicketSubject ?? '' },
        t
      );
    case 'activity_report_submit_reminder':
      return translate('activity_report_submit_reminder', {}, t);
    case 'activity_report_validated':
      return translate(
        'activity_report_validated',
        { employeeName: notification.employeeName ?? notification.triggeredByUser ?? '' },
        t
      );
    case 'activity_report_overdue':
      return translate(
        'activity_report_overdue',
        { employeeName: notification.employeeName ?? notification.triggeredByUser ?? '' },
        t
      );
    case 'payment_plan_overdue':
      return translate(
        'payment_plan_overdue',
        {
          clientName: notification.clientName ?? '',
          amount: formatNumber(notification.amountRemaining ?? 0),
          receiptSuffix: notification.saleReceiptNumber
            ? ` (${notification.saleReceiptNumber})`
            : '',
        },
        t
      );
    case 'licence_payment_due_7d':
    case 'licence_payment_due_1d':
    case 'licence_payment_blocked':
      return translate(type, {}, t);
    case 'messages_clients_ready':
    case 'messages_clients_no_phone':
      return translate(type, { clientName: notification.clientName ?? '' }, t);
    case 'manufacturing_order_on_hold':
    case 'manufacturing_order_done':
      return translate(type, {}, t);
    case 'inventory_audit_started':
    case 'inventory_audit_to_close':
      return translate(
        type,
        {
          siteSuffix: notification.siteName ? ` — ${notification.siteName}` : '',
        },
        t
      );
    case 'inventory_loss_created':
      return translate(type, { productName: notification.productName ?? '' }, t);
    case 'performance_sheets_generated': {
      const m = notification.performanceMonth;
      const y = notification.performanceYear;
      const period =
        typeof m === 'number' && typeof y === 'number'
          ? `${String(m).padStart(2, '0')}/${y}`
          : '';
      return translate(
        type,
        {
          period,
          count: Number(notification.createdCount ?? 0),
        },
        t
      );
    }
    case 'performance_sheet_validated': {
      const m = notification.performanceMonth;
      const y = notification.performanceYear;
      const period =
        typeof m === 'number' && typeof y === 'number'
          ? `${String(m).padStart(2, '0')}/${y}`
          : '';
      return translate(type, { period }, t);
    }
    default:
      return notification.message || '';
  }
}

export function getNotificationTypeLabel(
  type: string,
  t?: TranslateFn
): string {
  const label = translate(`typeLabel.${type}`, { defaultValue: '' }, t);
  return label || type;
}
